import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// **** Material UI Components ****
import { FormControl as MuiFormControl } from "@mui/material";

// **** Custom Components and Services ****
import { ResetFilter } from "app/shared/ui/FilterPanel/ResetFilter";
import {
  FacultyTrainingRoles,
  STORAGE_TYPE,
  STORAGE_NAMES,
} from "app/shared/constants";
import { Autocomplete } from "app/shared/ui/Autocomplete/Autocomplete";
import { AutocompleteOption } from "app/shared/ui/Autocomplete";
/** Services */
import { useSelectedSubmission } from "app/services/selectedSubmissionService";
import { getFacultyFiltersOptionValues } from "app/services/facultyService";
import { useFacultyDemographicsContext } from "app/services/facultyDemographicsContext";
import { useBrowserStorage } from "app/services/StorageService/StorageHelper";
/** Context and Component to show the error on UI */
import { useAlerts } from "common";

const initialFacultyFilterValues = {
  facultyName: "",
  degreeCode: "",
  rankCode: "",
  trainingRole: "",
  departmentCode: "",
};

// ************************* FacultyFilter *************************
export const FacultyFilter = ({ submissionUpdate }) => {
  const { t } = useTranslation();
  const { saveItem } = useBrowserStorage(STORAGE_TYPE.SESSION);
  // **** Selected Submission Id ****
  const [getSelectedSubmission] = useSelectedSubmission().value;
  const [getSelectedSubmissionName] = useSelectedSubmission().name;
  const selectedSubmission = getSelectedSubmission();
  const [degreeList, setDegreeList] = useState([]);
  const [rankList, setRankList] = useState([]);
  const [programList, setProgramList] = useState([]);
  const [trainingRoleList, setTrainingRoleList] = useState([]);

  const { setAlert, clearAlert } = useAlerts();

  // **** Faculty Context Object ****
  const { facultyPreference, setFacultyPreference } =
    useFacultyDemographicsContext();

  // **** Filter Panel Form Fields Values ****
  const facultyFiltersSelectedValues =
    facultyPreference.facultyDemographics.filters;

  // Reset Filter Form Values
  const resetFacultyFilterValues = () => {
    saveItem(
      STORAGE_NAMES.TGDS_FACULTY_DASHBOARD_FILTERS,
      initialFacultyFilterValues,
    );
    setFilterPreference({ ...initialFacultyFilterValues });
  };

  // ** On Page Load or Submission Update, reset Filter Values **
  useEffect(() => {
    if (selectedSubmission)
      getFacultyFiltersOptionValues(
        setDegreeList,
        setRankList,
        setProgramList,
        setTrainingRoleList,
        selectedSubmission,
        setAlert,
        clearAlert,
        getSelectedSubmissionName,
        t,
      );
    // eslint-disable-next-line
  }, [
    facultyPreference.facultyDemographics.delete,
    facultyPreference.facultyDemographics.addFaculty,
    selectedSubmission,
  ]);

  // *** Helper Function to Update Context Object, on Field Value Update ***
  const setFilterPreference = (fieldUpdate) => {
    setFacultyPreference({
      ...facultyPreference,
      facultyDemographics: {
        ...facultyPreference.facultyDemographics,
        filters: {
          ...facultyPreference.facultyDemographics.filters,
          ...fieldUpdate,
        },
        sort: {
          orderBy: "",
          order: "",
        },
        pagination: {
          page: "",
          pageSize: "",
        },
      },
    });
  };

  // **** On Dropdown Value change, update search results ****
  const handleFilterValueUpdate = (fieldName, fieldValue, updateFilterData) => {
    const updatedFilterValues = {
      ...facultyPreference.facultyDemographics.filters,
      [fieldName]: fieldValue ?? "",
    };

    if (updateFilterData) {
      setFilterPreference(updatedFilterValues);
    }
  };

  useEffect(() => {
    if (submissionUpdate) resetFacultyFilterValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionUpdate]);

  return (
    <>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="facultyNameAutoComplete"
          key="faculty-names"
          getOptionLabel={(option) => option?.displayText ?? ""}
          value={facultyFiltersSelectedValues.facultyName}
          label={t("faculty.mainView.filters.primaryName")}
          onChange={(e, value) => {
            handleFilterValueUpdate("facultyName", value || "", true);
          }}
          autoCompleteUrlPrefix={`/submissions/${selectedSubmission}/faculty/autocomplete?context=faculty`}
          filterOptions={(options) => options}
          renderOption={(props, state, { inputValue }) => (
            <AutocompleteOption
              option={props}
              inputValue={inputValue}
              state={state}
            />
          )}
        />
      </MuiFormControl>

      <MuiFormControl variant="outlined">
        <Autocomplete
          id="degreeAutoComplete"
          key="degree-autocomplete"
          options={degreeList || []}
          getOptionLabel={(option) =>
            option.shortName ? option.shortName : ""
          }
          value={facultyFiltersSelectedValues.degreeCode}
          isOptionEqualToValue={(op1, op2) => op1 === op2}
          clientSide
          label={t("faculty.mainView.filters.degree")}
          onChange={(e, value) => {
            handleFilterValueUpdate("degreeCode", value || "", true);
          }}
        />
      </MuiFormControl>

      <MuiFormControl variant="outlined">
        <Autocomplete
          id="rankAutoComplete"
          key="rank-autocomplete"
          options={rankList || []}
          getOptionLabel={(option) => (option.name ? option.name : "")}
          value={facultyFiltersSelectedValues.rankCode}
          isOptionEqualToValue={(op1, op2) => op1 === op2}
          clientSide
          label={t("faculty.mainView.filters.rank")}
          onChange={(e, value) => {
            handleFilterValueUpdate("rankCode", value || "", true);
          }}
        />
      </MuiFormControl>

      <MuiFormControl variant="outlined">
        <Autocomplete
          id="programAutoComplete"
          key="program-autocomplete"
          options={programList || []}
          value={facultyFiltersSelectedValues.departmentCode}
          getOptionLabel={(option) =>
            option.name ? `${option.name} (${option.code})` : ""
          }
          isOptionEqualToValue={(op1, op2) => op1 === op2}
          clientSide
          label={t("faculty.mainView.filters.program")}
          onChange={(e, value) => {
            handleFilterValueUpdate("departmentCode", value || "", true);
          }}
        />
      </MuiFormControl>

      <MuiFormControl variant="outlined">
        <Autocomplete
          id="trainingAutocomplete"
          key="training-autocomplete"
          options={trainingRoleList || []}
          getOptionLabel={(option) => t(FacultyTrainingRoles[option])}
          value={t(facultyFiltersSelectedValues.trainingRole)}
          isOptionEqualToValue={(op1, op2) => op1 === op2}
          clientSide
          label={t("faculty.mainView.filters.trainingRole")}
          onChange={(e, value) => {
            handleFilterValueUpdate("trainingRole", value || "", true);
          }}
        />
      </MuiFormControl>
      <ResetFilter resetFn={resetFacultyFilterValues} />
    </>
  );
};
